import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  User,
  Mail,
  Calendar,
  Shield,
  Crown,
  ArrowLeft,
  Edit2,
  Check,
  X,
  Loader2,
  Star,
  Wallet,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { UserStatsPanel, GameHistory } from '@/features/stats'
import { useToast } from '@/hooks/use-toast'

export default function Profile() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { toast } = useToast()
  const { user, isDemo, requestMembership } = useAuth()
  const [isEditingName, setIsEditingName] = useState(false)
  const [newName, setNewName] = useState(user?.displayName || '')
  const [saving, setSaving] = useState(false)
  const [requestingMembership, setRequestingMembership] = useState(false)

  if (!user) {
    navigate('/')
    return null
  }

  const handleRequestMembership = async () => {
    setRequestingMembership(true)
    const result = await requestMembership()
    if (result.success) {
      toast({
        title: t('profile.membershipRequested'),
        description: t('profile.membershipRequestedDesc'),
      })
    } else {
      toast({
        title: 'Error',
        description: result.error,
        variant: 'destructive',
      })
    }
    setRequestingMembership(false)
  }

  const handleSaveName = async () => {
    if (!newName.trim()) return

    setSaving(true)
    // TODO: Implement name update in AuthContext
    // For now, just close the edit mode
    setTimeout(() => {
      setSaving(false)
      setIsEditingName(false)
    }, 500)
  }

  const formatDate = (date?: Date) => {
    if (!date) return '-'
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date)
  }

  const getRoleBadge = () => {
    if (user.isAdmin) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
          <Crown className="w-3 h-3" />
          DON
        </span>
      )
    }
    if (user.isPatriarch) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
          <Shield className="w-3 h-3" />
          Patriarca
        </span>
      )
    }
    return (
      <span className="px-2 py-1 bg-zinc-700/50 text-gray-400 rounded text-xs">
        Miembro
      </span>
    )
  }

  const getKycBadge = () => {
    switch (user.kycStatus) {
      case 'approved':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
            <Check className="w-3 h-3" />
            Verificado
          </span>
        )
      case 'pending':
        return (
          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">
            En revisión
          </span>
        )
      case 'rejected':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">
            <X className="w-3 h-3" />
            Rechazado
          </span>
        )
      default:
        return (
          <span className="px-2 py-1 bg-zinc-700/50 text-gray-400 rounded text-xs">
            Sin verificar
          </span>
        )
    }
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-4 mb-8"
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/lobby')}
          className="text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-cinzel text-2xl md:text-3xl text-casino-gold">
          {t('profile.myProfile')}
        </h1>
      </motion.div>

      <div className="space-y-6">
        {/* User Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-casino-gold" />
                Información Personal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400">Nombre</p>
                    {isEditingName ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          className="h-8 w-48"
                          autoFocus
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-green-400"
                          onClick={handleSaveName}
                          disabled={saving}
                        >
                          {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-400"
                          onClick={() => {
                            setIsEditingName(false)
                            setNewName(user.displayName || '')
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <p className="text-white font-medium">
                        {user.displayName || 'Sin nombre'}
                      </p>
                    )}
                  </div>
                </div>
                {!isEditingName && !isDemo && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsEditingName(true)}
                    className="text-gray-400 hover:text-white"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-400">Email</p>
                  <p className="text-white">{user.email}</p>
                </div>
              </div>

              {/* Member Since */}
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-400">Miembro desde</p>
                  <p className="text-white">{formatDate(user.createdAt)}</p>
                </div>
              </div>

              {/* Badges */}
              <div className="flex items-center gap-3 pt-2 border-t border-zinc-800">
                <div>
                  <p className="text-xs text-gray-400 mb-2">Estado</p>
                  <div className="flex items-center gap-2">
                    {getRoleBadge()}
                    {!isDemo && getKycBadge()}
                    {isDemo && (
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                        Modo Demo
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Membership Request Card - Only for Demo Users */}
        {isDemo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="border-casino-gold/30 bg-gradient-to-br from-casino-gold/5 to-transparent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-casino-gold">
                  <Crown className="w-5 h-5" />
                  {t('profile.becomeMember')}
                </CardTitle>
                <CardDescription>
                  {t('profile.membershipBenefitsDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Benefits */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                    <Wallet className="w-5 h-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-white text-sm font-medium">{t('profile.benefitDeposit')}</p>
                      <p className="text-gray-400 text-xs">{t('profile.benefitDepositDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                    <Users className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-white text-sm font-medium">{t('profile.benefitMultiplayer')}</p>
                      <p className="text-gray-400 text-xs">{t('profile.benefitMultiplayerDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                    <Star className="w-5 h-5 text-casino-gold mt-0.5" />
                    <div>
                      <p className="text-white text-sm font-medium">{t('profile.benefitVIP')}</p>
                      <p className="text-gray-400 text-xs">{t('profile.benefitVIPDesc')}</p>
                    </div>
                  </div>
                </div>

                {/* Request Button */}
                <div className="pt-4 border-t border-zinc-800">
                  <Button
                    variant="gold"
                    className="w-full"
                    onClick={handleRequestMembership}
                    disabled={requestingMembership}
                  >
                    {requestingMembership ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('profile.requesting')}
                      </>
                    ) : (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        {t('profile.requestMembershipBtn')}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    {t('profile.requestMembershipNote')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Stats Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <UserStatsPanel userId={user.uid} isDemo={isDemo} />
        </motion.div>

        {/* Game History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <GameHistory userId={user.uid} isDemo={isDemo} />
        </motion.div>
      </div>
    </div>
  )
}
